import { apiService } from './api';
import {
  SageCustomerDetails,
  LinkExistingSageCustomerRequest,
  SageDriftDto,
  SageReconcileDirection,
} from '../types/sage';

/**
 * Reads the HTTP status off either error shape:
 *   - raw AxiosError (`err.response.status`)
 *   - post-PR2 normalized envelope error (`err.httpStatus`)
 */
const statusOf = (err: unknown): number | undefined => {
  if (typeof err !== 'object' || err === null) return undefined;
  const e = err as { response?: { status?: number }; httpStatus?: number };
  return e.response?.status ?? e.httpStatus;
};

/**
 * Service for finding, searching, linking and unlinking Sage customers for DRIS entities.
 * Used for Farmers and Agents (which are Sage Customers, not Suppliers).
 */
export const sageCustomerLinkingService = {
  /**
   * Find an existing Sage customer by EXACT name match for the entity.
   * @param entityType The DRIS entity type (e.g. 'Farmer', 'Agent')
   * @param entityId The DRIS entity ID
   * @returns The matching Sage customer details, or null if genuinely not found (404).
   * @throws Rethrows any non-404 error (403, network, 5xx) so callers can surface a real error
   *         instead of silently treating it as "no match".
   */
  findExistingCustomer: async (
    entityType: string,
    entityId: number,
    sageName?: string,
  ): Promise<SageCustomerDetails | null> => {
    try {
      const response = await apiService.get<SageCustomerDetails>(
        `/sage-customer-linking/${entityType}/${entityId}/find-customer`,
        sageName ? { sageName } : undefined,
      );
      return response.data ?? null;
    } catch (err) {
      if (statusOf(err) === 404) return null;
      throw err;
    }
  },

  /**
   * Search Sage customers by partial, case-insensitive name.
   * @param name The (partial) name to search for.
   * @returns Matching customers, or [] when the name is blank or nothing matches.
   */
  searchCustomers: async (name: string): Promise<SageCustomerDetails[]> => {
    const trimmed = name.trim();
    if (!trimmed) return [];
    const response = await apiService.get<SageCustomerDetails[]>(
      `/sage-customer-linking/customers/search`,
      { name: trimmed },
    );
    return response.data ?? [];
  },

  /**
   * Link an existing Sage customer to a DRIS entity and optionally sync data.
   * @param entityType The DRIS entity type
   * @param entityId The DRIS entity ID
   * @param request The link request containing sageCustomerId and syncDataToDris flag
   */
  linkExistingCustomer: async (
    entityType: string,
    entityId: number,
    request: LinkExistingSageCustomerRequest,
  ) => {
    return apiService.post(
      `/sage-customer-linking/${entityType}/${entityId}/link-customer`,
      request,
    );
  },

  /**
   * Unlink the entity's active Sage customer link. Idempotent (safe to call when already unlinked).
   * @param entityType The DRIS entity type
   * @param entityId The DRIS entity ID
   */
  unlinkCustomer: async (entityType: string, entityId: number): Promise<void> => {
    await apiService.post(
      `/sage-customer-linking/${entityType}/${entityId}/unlink-customer`,
    );
  },

  /**
   * Check whether the entity's DRIS details have drifted from its linked Sage record.
   *
   * Used as a pre-generation guard for financial documents. This MUST never break
   * generation: any error (network, 4xx, 5xx) is swallowed and returned as `null`,
   * which the guard treats as "no drift — generate immediately". The backend itself
   * also never throws on a Sage read error (it returns `checkPerformed: false`).
   *
   * @param entityType The DRIS entity type ('Farmer' | 'Agent' | 'Lab').
   * @param entityId The DRIS entity ID.
   * @returns The drift result, or null when the check could not be performed.
   */
  getDrift: async (entityType: string, entityId: number): Promise<SageDriftDto | null> => {
    try {
      const response = await apiService.get<SageDriftDto>(
        `/sage-customer-linking/${entityType}/${entityId}/drift`,
      );
      return response.data ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Reconcile the entity with its linked Sage record in the given direction.
   * @param entityType The DRIS entity type.
   * @param entityId The DRIS entity ID.
   * @param direction 'PushToSage' updates Sage from DRIS; 'PullToDris' updates DRIS from Sage.
   */
  reconcile: async (
    entityType: string,
    entityId: number,
    direction: SageReconcileDirection,
  ): Promise<void> => {
    await apiService.post(
      `/sage-customer-linking/${entityType}/${entityId}/reconcile`,
      { direction },
    );
  },
};
